import React, { useState } from 'react';
import { Star, Shield, MapPin, Clock, Users, ChevronRight } from 'lucide-react';
import { formatPrice } from '../data/mockData';

export const BADGE_COLORS = {
  'BEST SELLER': 'bg-[#F5A623] text-[#0E0E12]',
  'POPULER': 'bg-[#00C9B1] text-[#0E0E12]',
  'VIP': 'bg-purple-500 text-white',
  'HEMAT': 'bg-green-500 text-white',
  'KELUARGA': 'bg-blue-500 text-white',
  'EKSKLUSIF': 'bg-gradient-to-r from-[#F5A623] to-[#FDC15B] text-[#0E0E12]',
};

export default function PackageCard({ pkg, onViewDetail }) {
  const [imgError, setImgError] = useState(false);

  return (
    <div
      data-testid={`package-card-${pkg.id}`}
      className="card-hover rounded-2xl overflow-hidden dark:bg-[#1A1A24] bg-white dark:border dark:border-[#2D3748] border border-[#E2E8F0] flex flex-col"
    >
      {/* Image */}
      <div className="relative h-44 dark:bg-[#242430] bg-gray-100 overflow-hidden flex-shrink-0">
        {!imgError ? (
          <img
            src={pkg.image}
            alt={pkg.name}
            className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
            onError={() => setImgError(true)}
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-[#1A1A24] to-[#242430] flex items-center justify-center">
            <MapPin size={32} className="text-[#2D3748]" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0E0E12]/60 to-transparent" />

        {/* Badge */}
        <span className={`absolute top-3 left-3 px-2.5 py-1 rounded-full text-[10px] font-black tracking-wider ${BADGE_COLORS[pkg.badge] || 'bg-gray-600 text-white'}`}>
          {pkg.badge}
        </span>

        {/* Trust Score */}
        <div className="absolute top-3 right-3 flex items-center gap-1 bg-[#0E0E12]/70 backdrop-blur-sm px-2 py-1 rounded-full border border-[#F5A623]/30">
          <Shield size={10} className="text-[#F5A623]" />
          <span className="text-[10px] font-bold text-[#F5A623]">{pkg.trustScore}</span>
        </div>

        {/* Seats */}
        <div className="absolute bottom-3 left-3 text-[10px] text-white/80 bg-[#0E0E12]/60 px-2 py-0.5 rounded-full">
          {pkg.seats} kursi tersisa
        </div>
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        <p className="text-xs dark:text-gray-500 text-gray-400 mb-1 truncate">{pkg.provider}</p>
        <h3 className="font-bold dark:text-white text-[#1A202C] text-base mb-3 leading-snug line-clamp-2">
          {pkg.name}
        </h3>

        {/* Info Row */}
        <div className="flex items-center gap-3 mb-3">
          <div className="flex items-center gap-1 text-xs dark:text-gray-400 text-gray-500">
            <Clock size={12} />
            <span>{pkg.duration}</span>
          </div>
          <div className="flex items-center gap-1 text-xs dark:text-gray-400 text-gray-500">
            <Users size={12} />
            <span>{pkg.sold} sold</span>
          </div>
          <div className="flex items-center gap-1 ml-auto">
            <Star size={12} className="text-[#F5A623] fill-[#F5A623]" />
            <span className="text-xs font-semibold dark:text-gray-300 text-gray-600">{pkg.rating}</span>
            <span className="text-xs dark:text-gray-500 text-gray-400">({pkg.reviewCount})</span>
          </div>
        </div>

        {/* Highlights */}
        <div className="space-y-1 mb-4 flex-1">
          {pkg.highlights.slice(0, 2).map((h, i) => (
            <div key={i} className="flex items-start gap-2 text-xs dark:text-gray-400 text-gray-500">
              <div className="w-1.5 h-1.5 rounded-full bg-[#00C9B1] mt-1.5 flex-shrink-0" />
              <span className="line-clamp-1">{h}</span>
            </div>
          ))}
        </div>

        {/* Price + CTA */}
        <div className="flex items-end justify-between mt-auto pt-4 border-t dark:border-[#2D3748] border-[#E2E8F0]">
          <div>
            <p className="text-xs dark:text-gray-500 text-gray-400 mb-0.5">Mulai dari</p>
            <p className="text-lg font-black text-[#F5A623]">{formatPrice(pkg.price)}</p>
          </div>
          <button
            data-testid={`package-detail-btn-${pkg.id}`}
            onClick={() => onViewDetail && onViewDetail(pkg)}
            className="px-4 py-2 bg-[#00C9B1] text-[#0E0E12] font-semibold text-xs rounded-xl hover:brightness-110 transition-all hover:-translate-y-0.5 flex items-center gap-1"
          >
            Lihat Detail <ChevronRight size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
